import $ from 'jquery';
import SyosEvents from './utils/SyosEvents';
import SyosUiContainer from './SyosUiContainer';
import { loadModal } from '../AsyncLoader';
import SyosLegendRenderer from './SyosLegendRenderer';
import SyosPageComponent from './components/SyosPageComponent';
import dom from '../utils/dom';
import type SyosController from './SyosController';
import type IconRenderer from './seatIcons/IconRenderer';
import { SeatListResult } from './api/types';
import type AlertModalHandle from '../tnew/ui/Modal/AlertModalHandle';
import SyosConfig from './SyosConfig';
import { srOnlyElement } from '../utils/screen-reader/html';
import type SyosMediaQuery from './utils/SyosMediaQuery';
import { ISyosLabels } from './labels/types';
import ModalUtils from '../tnew/ui/Modal/ModalUtils';

const Class = {
  DESKTOP_CONTAINER: 'tn-syos-legend__desktop-container',
};

const LEGEND_CONTAINER_ID = 'tn-syos-legend-container';
const HEADING_ID = 'tn-syos-legend-heading';
const LEGEND_ID = 'tn-syos-legend';

export function getComponentElements(rootHeadingLevel: number, label: string) {
  const headingTag = `h${rootHeadingLevel + 1}`;

  const heading = srOnlyElement(headingTag, label, {
    id: HEADING_ID,
    class: ' tn-syos-legend__heading',
  });

  const el = document.createElement('div');

  el.innerHTML = `
    <div class="tn-syos-legend__legend-drawer">
      ${heading}
      <div class="${Class.DESKTOP_CONTAINER}"></div>
    </div>
  `;

  const headingEl = el.querySelector(`#${HEADING_ID}`)!;
  const desktopContainer = el.querySelector(`.${Class.DESKTOP_CONTAINER}`)!;

  return {
    el,
    heading: headingEl,
    desktopContainer,
  };
}

class SyosLegend extends SyosUiContainer {
  isLegendExpanded: boolean;

  legendRenderer: SyosLegendRenderer;

  $desktopContainer: JQuery<Element>;

  $inlineLegend?: HTMLElement;

  $modalLegend?: HTMLElement;

  modal?: AlertModalHandle;

  syosMediaQuery?: SyosMediaQuery;

  constructor(syosController: SyosController, iconRenderer: IconRenderer, config: SyosConfig, private labels: ISyosLabels, syosMediaQuery: SyosMediaQuery) {
    super(syosController, `#${LEGEND_CONTAINER_ID}`);

    this.syosMediaQuery = syosMediaQuery;

    this.isLegendExpanded = config.isLegendExpandedByDefault;

    this.legendRenderer = new SyosLegendRenderer(iconRenderer, config, labels);

    const { el, desktopContainer } = getComponentElements(config.rootHeadingLevel, labels.legendLabel);

    this.$el!.html(el);
    this.$el!.attr({
      'aria-hidden': 'true',
      'aria-labelledby': HEADING_ID,
    });

    this.$desktopContainer = $(desktopContainer);

    this.syosController?.on(SyosEvents.SCREEN_UPDATED, this.onScreenUpdated);
    this.syosController?.on(SyosEvents.SEAT_LIST_DATA_LOADED, this.onSeatListDataLoaded);
    this.syosController?.on(SyosEvents.LEGEND_DETAILS_TOGGLE, this.toggleDetails);

    syosMediaQuery.on(SyosEvents.TABLET_ENTER, this.onTabletEnter);
    syosMediaQuery.on(SyosEvents.TABLET_LEAVE, this.onTabletLeave);
  }

  onSeatListDataLoaded = (result: SeatListResult) => {
    if (this.$inlineLegend && this.$inlineLegend.parentElement) {
      dom.remove(this.$inlineLegend);
    }

    const legendData = {
      seatTypes: result.seatTypes,
      zones: result.screenZones,
    };

    this.$inlineLegend = this.legendRenderer.renderLegend(
      legendData,
      LEGEND_ID,
      'inline',
    );

    this.$modalLegend = this.legendRenderer.renderLegend(
      legendData,
      LEGEND_ID,
      'modal',
    );

    if (this.isLegendExpanded && !this.syosMediaQuery?.testIsTablet()) {
      this.expandDetails();
    }

    this.show();
  };

  toggleDetails = () => {
    if (this.syosMediaQuery?.testIsTablet()) {
      this.openModal();
      return;
    }

    if (this.isLegendExpanded) {
      this.collapseDetails();
    } else {
      this.expandDetails();
    }
  };

  private createModalCloseButton(): HTMLButtonElement {
    const labelBtnClose = this.labels.modalCloseButtonLabel;
    const closeButton = ModalUtils.makePrimaryButton(labelBtnClose)[0];
    return closeButton;
  }

  openModal = async () => {
    const Modal = await loadModal();
    const closeButton = this.createModalCloseButton();

    const onModalCloseButtonClick = () => {
      this.modal?.close();
    };

    closeButton.addEventListener('click', onModalCloseButtonClick);

    this.modal = Modal.open({
      title: SyosPageComponent.legendLabel,
      bodyContents: this.$modalLegend,
      beforeClose: () => {
        closeButton.removeEventListener('click', onModalCloseButtonClick);
        this.syosController?.emit(SyosEvents.LEGEND_DETAILS_HIDE);
        this.modal = undefined;
      },
      footerContents: [closeButton],
    });
  };

  onTabletEnter = () => {
    if (this.isLegendExpanded) {
      this.collapseDetails();
    }
  };

  onTabletLeave = () => {
    if (this.modal) {
      this.modal.close();
    }
  };

  onScreenUpdated = ({ screen }: { screen: Screen }) => {
    if (!this.syosMediaQuery) {
      return;
    }

    if (screen) {
      if (this.isLegendExpanded && !this.syosMediaQuery.testIsTablet()) {
        this.expandDetails();
      }
    } else {
      this.hide();
    }
  };

  expandDetails() {
    this.isLegendExpanded = true;

    this.$el?.attr({
      'aria-hidden': 'false',
      tabindex: '0',
    });

    if (this.$inlineLegend) {
      this.$desktopContainer.append(this.$inlineLegend);
    }

    this.syosController?.emit(SyosEvents.LEGEND_DETAILS_SHOW);
  }

  collapseDetails = () => {
    this.isLegendExpanded = false;

    this.$el?.attr({
      'aria-hidden': 'true',
      tabindex: '-1',
    });

    this.syosController?.emit(SyosEvents.LEGEND_DETAILS_HIDE);
  };

  show() {
    this.$el?.show();
  }

  hide() {
    this.$el?.hide();
  }

  destroy() {
    this.syosMediaQuery?.removeListener(SyosEvents.TABLET_ENTER, this.onTabletEnter);
    this.syosMediaQuery?.removeListener(SyosEvents.TABLET_LEAVE, this.onTabletLeave);
    this.syosMediaQuery = undefined;

    super.destroy();
  }
}

export default SyosLegend;

export { LEGEND_ID };
